/**
 * User: Rob Richard
 * Date: 5/6/14
 * Time: 12:07 PM
 */

"use strict";

const _ = require('underscore');
const Transaction = require('bunsen/models/ecomm/Transaction');
const PaymentAuthorization = require('bunsen/models/ecomm/PaymentAuthorization');
const { momentLocales } = require('web-mc');
const Offer = require('bunsen/models/ecomm/Offer');

module.exports = {
    isAccepted: function () {
        return this.getMostRecentOffer().isAccepted();
    },
    isAwaitingManualReview: function () {
        const summary = this.get('summary');
        const checkoutPaymentAuthorization = summary && summary.get('checkoutPaymentAuthorization');
        return checkoutPaymentAuthorization && checkoutPaymentAuthorization.get('status') === PaymentAuthorization.STATUS_PENDING_ADMIN_APPROVAL;
    },
    isPrivateOffer: function () {
        const offer = this.getFirstOffer();
        return offer && offer.isDealerInitiatedOffer();
    },
    isPreCheckout: function () {
        return this.get('summary.checkoutPaymentAuthorization') ? false : true;
    },
    hasSingleOffer: function () {
        const offers = this.get('offers');
        return offers && offers.length === 1;
    },
    hasMultipleOffers: function () {
        const offers = this.get('offers');
        return offers && offers.length > 1;
    },
    hasTradeBuyer: function () {
        return this.get('tradeBuyer') === 'Y';
    },
    buyerCanAcceptPrivateOffer: function () {
        if (!this.isPrivateOffer()) {
            return false;
        }
        if (this.get('status') !== Transaction.STATUS_NEGOTIATING) {
            return false;
        }
        if (_.result(this.getMostRecentOffer(), 'isExpired')) {
            return false;
        }
        if (this.get('summary.checkoutPaymentAuthorization')) {
            return false;
        }
        return true;
    },
    // orders marked as sold from the omt do not get status=CANCELED
    isPrivateOfferCanceled: function () {
        if (this.isCanceled()) {
            return true;
        }
        if (this.isPending() || this.get('status') === Transaction.STATUS_CLOSED) {
            return false;
        }
        return _.result(this.get('item'), 'isItemSold');
    },
    isReadyForBalanceDue : function () {
        const paymentRequiredQuote = this.getPaymentRequiredQuote();
        return !!(this.isInProgress() && (paymentRequiredQuote || this.hasBuyerOrderMod()));
    },
    isNegotiable: function () {
        return this.get('transactionType') === Transaction.TYPE_NEGOTIABLE;
    },
    isPreAuthNegotiation: function () {
        return this.get('transactionType') === Transaction.TYPE_PRE_AUTH_NEGOTIATION;
    },
    isBuyerInitiatedPreAuth: function () {
        const firstOffer = this.getFirstOffer();
        return this.isPreAuthNegotiation() && firstOffer && firstOffer.isBuyerInitiatedOffer();
    },
    isInProgress: function () {
        const inProgressStatuses = [
            Transaction.STATUS_NEGOTIATING
        ];
        return (_.contains(inProgressStatuses, this.get('status')));
    },
    isPending: function () {
        const inPendingStatuses = [Transaction.STATUS_PENDING];
        return (_.contains(inPendingStatuses, this.get('status')));
    },
    isConfirmed: function () {
        const confirmedStatuses = [Transaction.STATUS_PENDING, Transaction.STATUS_CLOSED];
        return (_.contains(confirmedStatuses, this.get('status')));
    },
    isClosed: function () {
        return this.get('status') === Transaction.STATUS_CLOSED;
    },
    isCanceled: function () {
        const canceledStates = [Transaction.STATUS_NOT_AVAILABLE, Transaction.STATUS_CANCELED];
        return (_.contains(canceledStates, this.get('status')));
    },
    isPreConfirmation: function () {
        return !this.isPreCheckout() && this.isInProgress();
    },
    isNotApproved: function () {
        const cancellationReasons = [
            Transaction.CANCELLATION_REASON_FRAUD,
            Transaction.CANCELLATION_REASON_CANCELED_BEFORE_APPROVED
        ];

        if (this.isAwaitingManualReview()) {
            return true;
        }

        if (_.contains(cancellationReasons, this.get('cancellationReason'))) {
            return true;
        }

        return false;
    },
    isReadyToMarkDealerPaid: function () {
        const paymentAuth = this.getOfferPaymentAuthorization();
        const hasPaymentAuthorization = paymentAuth && paymentAuth.isCaptured();
        const isPending = this.isPending();
        const canceled = this.isCanceled();
        const dealerDisbursement = this.getDealerDisbursement();

        return hasPaymentAuthorization &&
               !canceled &&
               isPending &&
               dealerDisbursement &&
               dealerDisbursement.isNewStatus();
    },
    canComputeAdjustments: function () {
        return !!this.getMostRecentOffer().get('quantity');
    },
    isPendingPaymentDeclined: function () {
        const paymentAuth = this.getOfferPaymentAuthorization();
        if (paymentAuth) {
            return paymentAuth.hasPaymentDeclined();
        } else {
            return false;
        }
    },
    getOfferDetails() {
        let mostRecentOffer;
        let offerAmounts;
        let offerExpDateTxt;

        if (!this) {
            return {};
        }
        mostRecentOffer = this.getMostRecentOffer();
        if (!mostRecentOffer) {
            return {};
        }

        if (mostRecentOffer.get('expirationDate') && this._needExpDate()) {
            offerExpDateTxt = momentLocales.inlineDatesAdverb(mostRecentOffer.get('expirationDate')).fromNow();
        }
        offerAmounts = mostRecentOffer.get('totalAmount');
        return {
            offer: mostRecentOffer,
            offerAmounts, offerExpDateTxt
        };
    },

    _needExpDate() {
        const mostRecentOffer = this.getMostRecentOffer();
        if (!this.isPreAuthNegotiation() || this.isCanceled()) {
            return false;
        }
        if (mostRecentOffer.isSubmitted()) {
            return true;
        }
        if (mostRecentOffer.offerDisplayStatus({transaction: this}) === Offer.DisplayOfferStatus.dealerAccepted) {
            return true;
        }
        return false;
    },

    _getCounteredOffers() {
        return this.get('offers').where({status: Offer.STATUS_COUNTERED});
    },

    isMostRecentlyDealerCountered() {
        return _.chain(this._getCounteredOffers()).last().result('isBuyerInitiatedOffer').value();
    },

    isMostRecentlyBuyerCountered() {
        return _.chain(this._getCounteredOffers()).last().result('isDealerInitiatedOffer').value();
    },

    isBuyerOfferWithReserve() {
        // currently, this method is the same as isNegotiable and only checks if the transaction type is negotiable
        // created this method in case the isNegotiable method gets updated above to include other transaction types
        return this.get('transactionType') === Transaction.TYPE_NEGOTIABLE;
    },

    isFullPriceOrder() {
        // in the 5.48 BE ecom release, all reserve-less offers will have a new type
        // the only tx of type non-negotiable will be full-priced orders
        return this.get('transactionType') === Transaction.TYPE_NON_NEGOTIABLE;
    },

    isBuyerInitiatedTx() {
        return _.result(this.getFirstOffer(), 'isBuyerInitiatedOffer');
    }
};



// WEBPACK FOOTER //
// ./~/bunsen/models/mixins/ecomm/Transaction/transactionStatusMixin.es.js